import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IListing, ListingsService, AlertService } from '@out-web/core';
import {
  ButtonComponent,
  AlertComponent,
} from '@out-web/components';
import { CapitalizeWithSpacesPipe } from '@out-web/core';

interface FileWithPreview {
  file: File;
  preview: string;
  status: 'uploading' | 'success' | 'error' | null;
  uploadProgress?: number;
  name?: string; // Add name property
  size?: number; // Add size property
}

@Component({
  selector: 'out-upload-imgaes',
  standalone: true,
  imports: [
    CommonModule,
    CapitalizeWithSpacesPipe,
    RouterLink,
    ButtonComponent,
    AlertComponent,
  ],
  templateUrl: './upload-imgaes.component.html',
  styleUrl: './upload-imgaes.component.scss',
})
export class UploadImgaesComponent implements OnInit {
  listingId: string | null = null;
  isSaving = false;
  isSavingToContinue = false;
  outOfStep = false;
  isLoadingInitialData = true;
  listing: IListing | null = null;
  files: FileWithPreview[] = [];
  errorMessage: string | null = null;
  isDragging = false;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  readonly MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB in bytes
  readonly MAX_FILES = 50;
  isUploading = false; // Add a property to track upload state

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private meta: Meta,
    private titleService: Title,
    private listingsService: ListingsService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.setMetaData();
    this.checkOutOfStep();
    this.fetchListingData();
  }

  private setMetaData() {
    this.route.paramMap.subscribe((params) => {
      this.listingId = params.get('id');
    });
    this.titleService.setTitle(
      'Sell My Tesla| Sell Your Tesla | Only Used Tesla'
    );
    this.meta.addTags([
      {
        name: 'description',
        property: 'description',
        content:
          'Only Used Tesal | A Place Exclusively Dedicated To Buying And Selling Used Teslas',
        vmid: 'description',
      },
      {
        name: 'og:description',
        property: 'og:description',
        content:
          'Only Used Tesla | A Place Exclusively Dedicated To Buying And Selling Used Teslas',
        hid: 'og:description',
      },
      {
        name: 'keywords',
        content:
          'Used Tesla, Buy Tesla Canada, Tesla Model 3, Tesla Model S, Used Electric Cars, Affordable Tesla, Certified Tesla',
      },
      {
        name: 'author',
        content: 'Only Used Tesla',
      },
    ]);
  }

  private checkOutOfStep() {
    this.route.queryParamMap.subscribe((params) => {
      this.outOfStep = params.get('out-of-step') === 'true';
    });
  }

  private fetchListingData() {
    this.isLoadingInitialData = true;
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.listingId = id;

      if (id) {
        this.listingsService.fetchListing(id).subscribe({
          next: (data: IListing) => {
            this.listing = data;
          },
          error: (error) => {
            console.error('Error fetching listing:', error);
          },
          complete: () => {
            this.isLoadingInitialData = false;
          },
        });
      }
    });
  }

  saveListingAndContinue() {
    this.isSavingToContinue = true;
    if (!this.listingId || this.isUploading) return; // Check if uploading

    if (this.files.length === 0) {
      this.errorMessage = 'Please upload at least one image before saving.'; // Set error message
      this.isSavingToContinue = false;
      return;
    }

    this.listingsService.closeImageUpload(this.listingId).subscribe({
      next: () => {
        this.Alert('success', 'Success', 'Images uploaded successfully');
        this.router.navigate(['/your-tesla/listing/checkout', this.listingId]);
        this.isSavingToContinue = false;
      },
      error: (error) => {
        const errorMessage = error?.error?.message || error?.message || 'An unknown error occurred';
        console.error('Error uploading image:', errorMessage);
        this.Alert('error', 'Error', 'Failed to upload images' + errorMessage);
        this.isSavingToContinue = false;
      },
    });
  }

  Save() {
    this.isSaving = true;
    if (!this.listingId || this.isUploading) return; // Check if uploading

    if (this.files.length === 0) {
      this.errorMessage = 'Please upload at least one image before saving.'; // Set error message
      this.isSaving = false;
      return;
    }

    this.listingsService.closeImageUpload(this.listingId).subscribe({
      next: () => {
        this.Alert('success', 'Success', 'Images uploaded successfully');
        this.router.navigate(['/account']);
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error uploading image:', error);
        this.Alert('error', 'Error', 'Failed to upload images' + error);
        this.isSaving = false;
      },
    });
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(input.files);
      // Reset the input so the same file can be selected again
      input.value = '';
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  addFiles(fileList: FileList) {
    if (this.files.length + fileList.length > this.MAX_FILES) {
      this.Alert('error', 'Error', `Maximum ${this.MAX_FILES} images allowed`);
      return;
    }

    const filesToUpload: FileWithPreview[] = []; // Array to hold files to upload

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (!file.type.startsWith('image/')) {
        this.Alert('error', 'Error', 'Only image files are allowed');
        continue;
      }

      if (file.size > this.MAX_FILE_SIZE) {
        this.Alert('error', 'Error', `File ${file.name} exceeds maximum size of 15MB`);
        continue;
      }

      const isDuplicate = this.files.some(
        (existingFile) =>
          existingFile.file.name === file.name &&
          existingFile.file.size === file.size
      );

      if (!isDuplicate) {
        const fileWithPreview: FileWithPreview = {
          file,
          preview: URL.createObjectURL(file),
          status: 'uploading', // Set initial status to 'uploading'
          name: file.name,
          size: file.size,
        };

        this.files.push(fileWithPreview); // Add to the files array
        filesToUpload.push(fileWithPreview); // Add to the upload queue
      }
    }

    this.processUploadQueue(filesToUpload); // Start processing the upload queue
  }

  private processUploadQueue(filesToUpload: FileWithPreview[]) {
    const uploadNextFile = (index: number) => {
      if (index >= filesToUpload.length) {
        this.isUploading = false; // Reset uploading state after all files processed
        return; // All files processed
      }

      const fileWithPreview = filesToUpload[index];
      this.isUploading = true; // Set uploading state

      this.uploadFile(fileWithPreview).then(() => {
        uploadNextFile(index + 1); // Process next file
      });
    };

    uploadNextFile(0); // Start processing the upload queue
  }

  private uploadFile(fileWithPreview: FileWithPreview): Promise<void> {
    return new Promise((resolve) => {
      if (!this.listingId) {
        this.Alert('error', 'Error', 'No listing ID found');
        resolve();
        return;
      }

      fileWithPreview.status = 'uploading';

      this.listingsService
        .updateImage(this.listingId, fileWithPreview.file)
        .subscribe({
          next: () => {
            fileWithPreview.status = 'success';
            setTimeout(() => {
              fileWithPreview.status = null;
              resolve(); // Resolve promise after success
            }, 3000);
          },
          error: (error) => {
            console.error('Error uploading image:', error);
            fileWithPreview.status = 'error';
            this.Alert('error', 'Error', 'Failed to upload image');
            resolve(); // Resolve promise after error
          },
        });
    });
  }

  removeFile(fileWithPreview: FileWithPreview, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    if (!this.listingId) {
      this.Alert('error', 'Error', 'No listing ID found');
      return;
    }

    fileWithPreview.status = 'uploading';

    this.listingsService
      .deleteImage(this.listingId, fileWithPreview.file.name)
      .subscribe({
        next: () => {
          URL.revokeObjectURL(fileWithPreview.preview);
          this.files = this.files.filter((f) => f !== fileWithPreview);
          this.Alert('success', 'Success', 'Image removed successfully');
        },
        error: (error) => {
          console.error('Error removing image:', error);
          fileWithPreview.status = 'error';
          this.Alert('error', 'Error', 'Failed to remove image');
          setTimeout(() => {
            fileWithPreview.status = null;
          }, 3000);
        },
      });
  }

  Alert(
    alertType: 'error' | 'success' | 'info' | 'warning' | 'white',
    alertTitle: string,
    alertMessage: string
  ) {
    this.alertService.showAlert({
      title: alertTitle,
      message: alertMessage,
      type: alertType,
      duration: 5000,
      position: 'top-right',
    });
  }
}
